/**
 * Forge 3.1 · Project Universe — data hook
 * Fetches the real graph from /api/kernel/universe/graph and prepares
 * it for d3-force-3d layout consumption.
 */
import { useEffect, useMemo, useState } from "react";

const API = "";

export function useUniverseGraph({ scope = "all", maxNodes = 500 } = {}) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    nodes: [],
    edges: [],
    stats: null,
    modules: [],
  });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    (async () => {
      try {
        const url = `${API}/api/kernel/universe/graph?scope=${encodeURIComponent(
          scope
        )}&max_nodes=${maxNodes}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        // Prepare nodes with initial positions for d3-force-3d
        // Distribute in a rough sphere so the first tick isn't chaos
        const nodes = data.nodes.map((n, i) => {
          const phi = Math.acos(-1 + (2 * i) / data.nodes.length);
          const theta = Math.sqrt(data.nodes.length * Math.PI) * phi;
          const r = 80 + Math.random() * 120;
          return {
            ...n,
            x: r * Math.cos(theta) * Math.sin(phi),
            y: r * Math.sin(theta) * Math.sin(phi),
            z: r * Math.cos(phi),
          };
        });

        const nodeIndex = new Map(nodes.map((n) => [n.id, n]));
        const edges = data.edges
          .map((e) => ({
            ...e,
            source: nodeIndex.get(e.source),
            target: nodeIndex.get(e.target),
          }))
          .filter((e) => e.source && e.target);

        setState({
          loading: false,
          error: null,
          nodes,
          edges,
          stats: data.stats,
          modules: data.modules || [],
          legend: data.legend || {},
        });
      } catch (e) {
        if (!cancelled) {
          setState((s) => ({ ...s, loading: false, error: e.message || "error" }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scope, maxNodes]);

  return state;
}

/**
 * Groups nodes by module for the module-filter legend UI.
 */
export function useModuleColors(nodes) {
  return useMemo(() => {
    const map = new Map();
    nodes.forEach((n) => {
      if (!map.has(n.module) && n.type === "module") {
        map.set(n.module, n.color);
      }
    });
    // fallback colors for modules without a "module" node
    const palette = [
      "#8b5cf6", "#38bdf8", "#f59e0b", "#10b981", "#ef4444",
      "#f472b6", "#22d3ee", "#eab308", "#a3e635", "#fb923c",
    ];
    let idx = 0;
    nodes.forEach((n) => {
      if (!map.has(n.module)) {
        map.set(n.module, palette[idx++ % palette.length]);
      }
    });
    return Object.fromEntries(map);
  }, [nodes]);
}
